import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Theme, SxProps } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useSearchParams } from 'react-router-dom';
import CustomButton, { ICustomButton } from '../Button/CustomButton';
import { StyledBadge } from '../StyledBadge/StyledBadge';

interface ICustomSegmentedControl {
    onHandleClick: (tab: string, index: number) => void;
    tabs: string[];
    sx?: SxProps<Theme>;
    typographySx?: SxProps<Theme>;
    containerStyle?: React.CSSProperties;
    innerContainerStyle?: React.CSSProperties;
    innerContainerSx?: SxProps<Theme>;
    initialIndex?: number;
    highlights?: number[];
    enableButton?: boolean;
    buttonProps?: ICustomButton;
    disregardParam?: boolean;
    /** If a feld is required then highlight it with a red dot */
    requiredFields?: string[];
    /** Function to check if a field is filled/valid */
    isFieldFilled?: (fieldName: string) => boolean;
}

const CustomSegmentedControl: React.FC<ICustomSegmentedControl> = ({
    onHandleClick,
    tabs,
    sx,
    typographySx,
    containerStyle,
    highlights,
    initialIndex,
    enableButton,
    buttonProps,
    innerContainerStyle,
    innerContainerSx,
    disregardParam,
    requiredFields = [],
    isFieldFilled,
}) => {
    const { themeStyle } = useThemeContext();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [searchParams, setSearchParam] = useSearchParams();

    const handlePress = (tab: string, index: number) => {
        onHandleClick(tab, index);
        setCurrentIndex(index);
        if (!disregardParam) {
            setSearchParam({
                tab,
            });
        }
    };

    const handleSetTab = useCallback(() => {
        const params = Object.fromEntries([...searchParams]);
        if (params && params['tab']) {
            const tabIndex = tabs.findIndex((t, i) => t === params['tab']);
            if (tabIndex) {
                onHandleClick(params['tab'], tabIndex);
                setCurrentIndex(tabIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        if (initialIndex) {
            const tabName = tabs.find((_, i) => i === initialIndex);
            if (tabName) {
                onHandleClick(tabName, initialIndex);
            }
            setCurrentIndex(initialIndex);
        }
        handleSetTab();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Box
            style={{
                alignItems: 'center',
                width: '100%',
                // height: '60px',
                backgroundColor: theme[themeStyle].pure_black,
                ...containerStyle,
            }}
            sx={{
                position: { xs: 'absolute', sm: 'static' },
                top: '135px',
                display: {
                    xs: 'block',
                    sm: 'flex',
                },
                left: 0,
                right: 0,
                justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                paddingLeft: { xs: '20px', sm: '70px' },
                boxShadow: `0 1.5px 2px -2px ${theme[themeStyle].placeholderGray}`,
                borderBottom: `1px ${theme[themeStyle].placeholderGray}`,
                overflowX: 'scroll',
                height: {
                    xs: '60px',
                },
                ...sx,
            }}>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    position: 'relative',
                    ...innerContainerStyle,
                }}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    sx={{
                        gap: { xs: '20px', sm: '40px' },
                        width: {
                            xs: '100',
                            sm: 'fit-content',
                        },
                        height: '100%',
                        ...innerContainerSx,
                    }}>
                    {tabs.map((tab, index) => {
                        const isRequired = requiredFields.includes(tab);
                        const isFilled = isFieldFilled ? isFieldFilled(tab) : false;
                        const showErrorState = isRequired && !isFilled;

                        return (
                            <Button
                                onClick={() => handlePress(tab, index)}
                                style={{
                                    backgroundColor: theme[themeStyle].transparent,
                                    height: '100%',
                                    width: 'fit-content',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 0,
                                    position: 'relative',
                                }}
                                key={index}
                                sx={{
                                    marginTop: 2.5,
                                    textTransform: 'none',
                                    borderBottom:
                                        currentIndex === index
                                            ? `1px solid ${theme[themeStyle].primary_orange}`
                                            : 'none',
                                    transition: 'all 0.3 ease-in-out',
                                    minWidth: '150px',
                                }}>
                                <Typography
                                    style={{
                                        textAlign: 'center',
                                        fontSize: 14,
                                        fontStyle: 'normal',
                                        fontWeight: '600',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'capitalize',
                                    }}
                                    sx={{
                                        color:
                                            currentIndex === index
                                                ? theme[themeStyle].primary_white
                                                : theme[themeStyle].form_label,
                                        transition: 'all 0.3 ease-in-out',
                                        width: 'fit-content',
                                        position: 'relative',
                                        ...typographySx,
                                    }}>
                                    {tab}
                                    {highlights?.includes(index) && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                height: 4,
                                                width: 4,
                                                borderRadius: 4,
                                                backgroundColor: theme[themeStyle].highlight,
                                                top: 2,
                                                right: -5,
                                            }}
                                        />
                                    )}
                                    {isRequired && (
                                        <StyledBadge
                                            errorState={showErrorState}
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            variant="dot"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: -10,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                                </Typography>
                            </Button>
                        );
                    })}
                </Box>
                {!!enableButton && (
                    <CustomButton
                        label="Submit"
                        customStyle={{ position: 'absolute', right: 10, top: '10%', width: '100px' }}
                        {...buttonProps}
                    />
                )}
            </Box>
        </Box>
    );
};

CustomSegmentedControl.defaultProps = {
    tabs: ['tab1', 'tab2', 'tab3'],
};

export { CustomSegmentedControl };
